import { useState, useEffect } from "react"
import { DollarSign, TrendingUp } from "lucide-react"
import { useCurrency } from "../contexts/CurrencyContext"
import { formatPriceInputValue, roundPriceAmount } from "../lib/pricing"
import PriceDisplay from "./PriceDisplay"

export default function PriceInputModal({
  isOpen,
  onClose,
  onConfirm,
  cardId,
  cardName = "this card",
  currentPrice = null
}) {
  const [price, setPrice] = useState("")
  const [useCurrentPrice, setUseCurrentPrice] = useState(true)
  const [condition, setCondition] = useState("Near Mint")
  const [quantity, setQuantity] = useState(1)
  const { currency, convertCadAmount } = useCurrency()

  const convertedCurrentPrice = currentPrice != null
    ? roundPriceAmount(convertCadAmount(currentPrice, currency), 2)
    : null

  useEffect(() => {
    if (isOpen) {
      // Reset form
      setPrice(convertedCurrentPrice != null ? formatPriceInputValue(convertedCurrentPrice, 2) : "")
      setUseCurrentPrice(true)
      setCondition("Near Mint")
      setQuantity(1)
    }
  }, [isOpen, convertedCurrentPrice])

  const handleConfirm = () => {
    const finalPrice = useCurrentPrice && convertedCurrentPrice != null
      ? convertedCurrentPrice
      : (price ? parseFloat(price) : null)

    onConfirm({
      price: finalPrice,
      currency,
      condition,
      quantity: parseInt(quantity)
    })
    onClose()
  }

  const getPriceChangeIcon = () => {
    // This would typically come from price history data
    // For now, just show a neutral icon
    return <TrendingUp className="w-4 h-4 text-gray-400" />
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-dark border border-purple/20 rounded-lg max-w-sm w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-purple/20">
          <h2 className="text-lg font-semibold text-cream">
            Add {cardName} to Collection
          </h2>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Current Price Display */}
          {currentPrice != null && (
            <div className="bg-gray-800/50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Current Market Price</span>
                {getPriceChangeIcon()}
              </div>
              <PriceDisplay amountCad={currentPrice} className="text-xl font-bold text-green-400" />
            </div>
          )}

          {/* Price Input */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Purchase Price
            </label>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="useCurrentPrice"
                  checked={useCurrentPrice}
                  onChange={(e) => setUseCurrentPrice(e.target.checked)}
                  className="w-4 h-4 text-purple bg-gray-700 border-gray-600 rounded focus:ring-purple"
                />
                <label htmlFor="useCurrentPrice" className="text-sm text-gray-300">
                  Use current market price
                </label>
              </div>

              {!useCurrentPrice && (
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Enter purchase price"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Condition */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Condition
            </label>
            <select
              value={condition}
              onChange={(e) => setCondition(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple"
            >
              <option value="Mint">Mint</option>
              <option value="Near Mint">Near Mint</option>
              <option value="Lightly Played">Lightly Played</option>
              <option value="Moderately Played">Moderately Played</option>
              <option value="Heavily Played">Heavily Played</option>
              <option value="Damaged">Damaged</option>
            </select>
          </div>

          {/* Quantity */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Quantity
            </label>
            <input
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-purple/20">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 px-4 py-2 bg-purple hover:bg-purple/90 text-white rounded-lg transition-colors"
          >
            Add to Collection
          </button>
        </div>
      </div>
    </div>
  )
}
